import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import { PaymentTypeStr, Stations } from '../../../../common/enums';
import {
  calculateGSTBreakdown,
  displayPrice,
} from '../../../../common/moneyFunctions';
import { groupOrderItems } from '../../../../common/orderFunctions';
import { Order, Station } from '../../../../common/types';

interface OrderPricingProps {
  orderData: Order;
  changeOrderStation: (stationName: string) => void;
  assignedStation: Station;
  submitOrder: (customerName: string) => void;
  setPaymentType: (val: PaymentTypeStr) => void;
  customerName: string;
  setCustomerName: (val: string) => void;
}

export default function OrderPricing({
  orderData,
  changeOrderStation,
  assignedStation,
  submitOrder,
  setPaymentType,
  customerName,
  setCustomerName
}: OrderPricingProps) {
  const theme = useTheme()
  const [showTooltip, setShowTooltip] = useState(false);

  const isOrderValid =
    orderData.orderItems.length > 0 && customerName.trim() !== '';
  const noCustomerName = !customerName.trim();
  const tooltipTitle = !isOrderValid
    ? `${orderData.orderItems.length === 0 ? `Add items to your order${noCustomerName ? ' & ' : '.'}` : ''}${noCustomerName ? 'Enter a customer name.' : ''}`
    : '';

  const handleButtonClick = () => {
    if (isOrderValid) {
      submitOrder(customerName);
    } else {
      setShowTooltip(true);
    }
  };


  const { total, subtotal, gst } = calculateGSTBreakdown(
    groupOrderItems(orderData.orderItems) || [],
  );

  return (
    <>
      <Stack spacing={1}>
        <Divider />
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body1">Subtotal</Typography>
          <Typography variant="body1">{displayPrice(subtotal)}</Typography>
        </Stack>

        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body1">GST (10%)</Typography>
          <Typography variant="body1">{displayPrice(gst)}</Typography>
        </Stack>

        <Divider />

        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h6">Total</Typography>
          <Typography variant="h6">{displayPrice(total)}</Typography>
        </Stack>

        <Stack direction="row" justifyContent="center">
          <TextField
            size="small"
            fullWidth
            label="Customer Name"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            sx={{
              flex: 0.5,
              marginBottom: theme.spacing(0.5),
              '& .MuiInputBase-input': {
                textAlign: 'center',
              },
            }}
          />
        </Stack>

        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant={assignedStation.name === Stations.StationOne ? 'contained' : 'outlined'}
            onClick={() => changeOrderStation(Stations.StationOne)}
          >
            Station 1
          </Button>
          <Button
            variant={assignedStation.name === Stations.StationTwo ? 'contained' : 'outlined'}
            onClick={() => changeOrderStation(Stations.StationTwo)}
          >
            Station 2
          </Button>
        </Stack>
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button onClick={() => setPaymentType(PaymentTypeStr.Card)} color={!orderData.paymentType ? 'error' : 'primary'} variant={orderData.paymentType === PaymentTypeStr.Card ? 'contained' : 'outlined'}>Card</Button>
          <Button onClick={() => setPaymentType(PaymentTypeStr.Cash)} color={!orderData.paymentType ? 'error' : 'primary'} variant={orderData.paymentType === PaymentTypeStr.Cash ? 'contained' : 'outlined'}>Cash</Button>
          <Button onClick={() => setPaymentType(PaymentTypeStr.Defer)} color={!orderData.paymentType ? 'error' : 'primary'} variant={orderData.paymentType === PaymentTypeStr.Defer ? 'contained' : 'outlined'}>Suspend</Button>
        </Stack>

        <Tooltip
          title={tooltipTitle}
          arrow
          open={showTooltip}
          onClose={() => setShowTooltip(false)}
        >
          <Button
            variant="contained"
            fullWidth
            color="primary"
            onClick={handleButtonClick}
            disabled={!orderData.paymentType}
          >
            Place Order
          </Button>
        </Tooltip>
      </Stack>
    </>
  );
}
